import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getBillingInvoicesByPatient,
  getBillingServices,
  createBillingInvoice,
  updateBillingInvoice,
  recordInvoicePayment,
  getPatientDeposits,
  applyAdvanceToInvoice,
} from '@/lib/api';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import InvoiceEditModal from '@/components/Patient/InvoiceEditModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { usePermission } from '@/hooks/usePermission';
import InvoiceTable from '@/components/Billing/InvoiceTable';
import { PaymentRecordModal, InvoiceViewModal } from '@/components/Billing/BillingModals';
import { SettleBalanceModal } from '@/components/Billing/SettleBalanceModal';
import { RecordDepositModal } from '@/components/Billing/RecordDepositModal';
import { RefundDialog } from '@/components/IPD/IPDChargesTab';
import PrintDepositReceiptModal, { type DepositReceipt } from '@/components/Billing/PrintDepositReceiptModal';
import { BillingSummaryCards, BillingListContainer } from '@/components/Billing/BillingUI';
import type { Invoice, BillingService } from '@/types';
import type { PatientDeposit, AdvanceRefund } from '@/lib/api';
import { formatDateTime } from '@/lib/formatDate';

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';

const PAYMENT_MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card',
  cheque: 'cheque', netbanking: 'netbanking',
  'bank transfer': 'netbanking',
  insurance: 'other', other: 'other', advance: 'advance',
};

function toPaymentMode(method: string | undefined): PaymentMode {
  return PAYMENT_MODE_MAP[(method || '').toLowerCase()] ?? 'cash';
}

interface BillingTabProps {
  patientId: string;
  patient: {
    id: string;
    name: string;
    phone?: string;
    address?: string;
    age?: number | string;
    gender?: string;
    mrn?: string;
    date_of_birth?: string;
  };
  doctorId: string;
  clinicId: string;
  /** Open the New bill dialog once on mount (the patient banner's "New bill"); cleared via onAutoOpened */
  autoOpenCreate?: boolean;
  onAutoOpened?: () => void;
  /** Called with the patient's balance due each time the account figures load (the banner's "₹ due" chip) */
  onAccountChange?: (outstanding: number) => void;
}

const ADVANCE_MODE_LABEL: Record<string, string> = { cash: 'Cash', upi: 'UPI', card: 'Card', cheque: 'Cheque', netbanking: 'Net banking', other: 'Other' };

export default function BillingTab({ patientId, patient, doctorId, clinicId, autoOpenCreate, onAutoOpened, onAccountChange }: BillingTabProps) {
  const [loading, setLoading]                   = useState(false);
  const [invoices, setInvoices]                 = useState<Invoice[]>([]);
  const [services, setServices]                 = useState<BillingService[]>([]);
  const [searchTerm, setSearchTerm]             = useState('');
  const [statusFilter, setStatusFilter]         = useState<string>('all');
  const [dateRange, setDateRange]               = useState<{ from: Date | null; to: Date | null }>({ from: null, to: null });
  const [amountRange, setAmountRange]           = useState({ min: '', max: '' });
  const [currentPage, setCurrentPage]           = useState(1);
  const [itemsPerPage, setItemsPerPage]         = useState(10);
  const [showCreateModal, setShowCreateModal]   = useState(false);
  const [showViewModal, setShowViewModal]       = useState(false);
  const [showEditModal, setShowEditModal]       = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrintModal, setShowPrintModal]     = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showSettleModal, setShowSettleModal]   = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showRefundAdvance, setShowRefundAdvance] = useState(false);
  const [depositReceipt, setDepositReceipt] = useState<DepositReceipt | null>(null);
  const [availableAdvance, setAvailableAdvance] = useState(0);
  // Advances taken and given back — listed so their receipts can be printed again
  const [advanceRows, setAdvanceRows] = useState<{ deposits: PatientDeposit[]; refunds: AdvanceRefund[] }>({ deposits: [], refunds: [] });
  const [outstanding, setOutstanding]           = useState(0);
  const [pendingAmount, setPendingAmount]       = useState(0);
  const [selectedInvoice, setSelectedInvoice]   = useState<Invoice | null>(null);

  const { formatCurrency } = useBillingUtils();
  // Creating, editing, taking payment, settling and advances all need billing:write (the server
  // enforces the same); without it the tab is read-only: view and print
  const { can } = usePermission();
  const canWrite = can('billing:write');

  useEffect(() => {
    Promise.all([loadInvoices(), loadDeposits(), loadServices()]);
  }, [patientId, clinicId]);

  useEffect(() => {
    if (!autoOpenCreate || !canWrite) return;
    setShowCreateModal(true);
    onAutoOpened?.();
  }, [autoOpenCreate, onAutoOpened]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, amountRange, invoices.length]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    toast({
      title: type === 'success' ? 'Success' : 'Error',
      description: message,
      variant: type === 'success' ? 'default' : 'destructive',
    });
  };

  // Status filter — direct comparison, no mapping needed
  const filteredInvoices = useMemo(() => invoices.filter(invoice => {
    const matchesSearch = !searchTerm ||
        invoice.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
        invoice.payment_status === statusFilter;

    let matchesDate = true;
    if (dateRange.from || dateRange.to) {
      const invoiceDate     = new Date(invoice.invoice_date);
      const invoiceDateOnly = new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate());

      if (dateRange.from && dateRange.to) {
        const from = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        const to   = new Date(dateRange.to.getFullYear(),   dateRange.to.getMonth(),   dateRange.to.getDate());
        matchesDate = invoiceDateOnly >= from && invoiceDateOnly <= to;
      } else if (dateRange.from) {
        const from = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
        matchesDate = invoiceDateOnly >= from;
      } else if (dateRange.to) {
        const to = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
        matchesDate = invoiceDateOnly <= to;
      }
    }

    const matchesAmountRange =
        (!amountRange.min || invoice.total_amount >= parseFloat(amountRange.min)) &&
        (!amountRange.max || invoice.total_amount <= parseFloat(amountRange.max));

    return matchesSearch && matchesStatus && matchesDate && matchesAmountRange;
  }), [invoices, searchTerm, statusFilter, dateRange, amountRange]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await getBillingInvoicesByPatient(patientId);
      setInvoices(data || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
      showNotification('error', 'Failed to load billing history');
    } finally {
      setLoading(false);
    }
  };

  const loadDeposits = async () => {
    try {
      const data = await getPatientDeposits(patientId, clinicId);
      setAvailableAdvance(Number(data?.available_advance || 0));
      setAdvanceRows({ deposits: data?.deposits ?? [], refunds: data?.refunds ?? [] });
      setOutstanding(Number(data?.outstanding || 0));
      onAccountChange?.(Number(data?.outstanding || 0));
      setPendingAmount(Number((data as any)?.pending_amount || 0));
    } catch (error) {
      console.error('Failed to load deposits:', error);
    }
  };

  // Newest first, as receipts: deposits in, advance refunds out
  const advanceList: (DepositReceipt & { id: string })[] = useMemo(() => [
    ...advanceRows.deposits.map(d => ({
      id: d.id, patient_id: patientId, clinic_id: clinicId, doctor_id: doctorId, amount: Number(d.amount),
      payment_mode: d.payment_mode, reference_number: d.reference_number, date: d.created_at, admission_number: d.admission_number,
      receipt_number: d.receipt_number,
    })),
    ...advanceRows.refunds.map(r => ({
      id: r.id, refund: true, reason: r.reason, patient_id: patientId, clinic_id: clinicId, doctor_id: doctorId, amount: Number(r.amount),
      payment_mode: r.refund_mode, reference_number: r.reference_number, date: r.created_at, admission_number: r.admission_number,
      receipt_number: r.receipt_number,
    })),
  ].sort((a, b) => String(b.date).localeCompare(String(a.date))), [advanceRows, patientId, clinicId, doctorId]);

  // Every money action changes both the bill list and the account figures
  const reloadAccount = () => Promise.all([loadInvoices(), loadDeposits()]);

  const loadServices = async () => {
    try {
      const data = await getBillingServices(clinicId, doctorId);
      setServices((data as any) || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      setLoading(true);

      const created = await createBillingInvoice({
        patient_id:      patientId,
        doctor_id:       doctorId,
        clinic_id:       clinicId,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items.map((item: any) => ({
          service_id:      item.service_id      || undefined,
          service_name:    item.service_name,
          category:        item.category        || undefined,
          item_type:       item.item_type        || 'other',
          quantity:        item.quantity,
          unit_price:      item.unit_price,
          discount_amount: item.discount_amount  || 0,
          tax_rate:        item.tax_rate         || 0,
          tax_amount:      item.tax_amount       || 0,
          total_price:     item.total_price,
          notes:           item.notes            || undefined,
        })),
        discount_amount:              invoiceData.discount    || 0,
        discount_type:                invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        discount_reason:              invoiceData.discountReason,
        tax_amount:                   invoiceData.taxAmount   || 0,
        notes:                        invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });

      // An advance taken with the bill: its receipt is the message's action. The patient is at the counter
      // with the cash just handed over, so the receipt can't wait; Book in the patient header stays one click
      // away (and carries the prescribed follow-up date, which this shortcut doesn't).
      const receipt: DepositReceipt | null = invoiceData.depositAmount ? {
        patient_id: patientId, clinic_id: clinicId, doctor_id: invoiceData.doctorId || doctorId,
        amount: invoiceData.depositAmount, payment_mode: invoiceData.depositPaymentMode,
        receipt_number: created?.deposit_receipt_number,
      } : null;
      toast({
        title: 'Success',
        description: receipt ? `Invoice created · ₹${receipt.amount.toLocaleString('en-IN')} kept as advance` : 'Invoice created successfully',
        duration: 10000, // long enough to reach the button (success messages otherwise go in 2s)
        action: receipt ? <ToastAction altText="Print advance receipt" onClick={() => setDepositReceipt(receipt)}>Print advance receipt</ToastAction> : can('appointments:write') ? (
            <ToastAction altText="Book follow-up" onClick={() => setShowFollowUpModal(true)}>
              Book follow-up
            </ToastAction>
        ) : undefined,
      });
      setShowCreateModal(false);
      await Promise.all([loadInvoices(), loadDeposits()]);
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({ title: 'Error', description: 'Failed to create invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    try {
      if (!selectedInvoice || amount <= 0) {
        showNotification('error', 'Please enter a valid payment amount');
        return;
      }
      if (amount > selectedInvoice.balance_due) {
        showNotification('error', 'Payment amount cannot exceed balance due');
        return;
      }
      setLoading(true);
      await recordInvoicePayment(selectedInvoice.id, {
        amount,
        payment_mode:     toPaymentMode(method),
        payment_date:     new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app:          meta?.upi_app          || undefined,
        bank_name:        meta?.bank_name         || undefined,
      });
      showNotification('success', 'Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedInvoice(null);
      await reloadAccount();
    } catch (error: any) {
      showNotification('error', error.response?.data?.error || error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowEditModal(true);
  };

  const handleUpdateInvoice = async (invoiceData: any) => {
    try {
      if (!selectedInvoice) return;
      setLoading(true);
      await updateBillingInvoice(selectedInvoice.id, invoiceData);
      toast({ title: 'Success', description: 'Invoice updated successfully' });
      setShowEditModal(false);
      setSelectedInvoice(null);
      await reloadAccount();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to update invoice', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAction = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleApplyAdvance = async (invoice: Invoice) => {
    const applyAmount = Math.min(availableAdvance, Number(invoice.balance_due));
    if (applyAmount <= 0) return;
    const confirmed = window.confirm(
      `Apply ₹${applyAmount.toLocaleString('en-IN')} advance to invoice ${invoice.invoice_number}?\n\nThis will reduce the advance balance by ₹${applyAmount.toLocaleString('en-IN')} and ${applyAmount >= Number(invoice.balance_due) ? 'mark the invoice as paid' : `leave ₹${(Number(invoice.balance_due) - applyAmount).toLocaleString('en-IN')} due on it`}.`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      await applyAdvanceToInvoice(patientId, clinicId, invoice.id, applyAmount);
      showNotification('success', `₹${applyAmount.toLocaleString('en-IN')} advance applied to ${invoice.invoice_number}`);
      await Promise.all([loadInvoices(), loadDeposits()]);
    } catch (error: any) {
      showNotification('error', error.message || 'Failed to apply advance');
    } finally {
      setLoading(false);
    }
  };

  // ── Stats ────────────────────────────────────────────────────────────────────
  // "Total Billed" — all non-cancelled invoices (not "Revenue" which is a clinic concept)
  const totalBilled = useMemo(() =>
    invoices
      .filter(inv => inv.status !== 'cancelled')
      .reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0),
  [invoices]);

  // Loading skeleton
  if (loading && invoices.length === 0) {
    return (
        <div className="space-y-3">
          <Skeleton className="h-[70px] rounded-lg" />
          <Skeleton className="h-9 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
    );
  }

  const handlePrintInvoice = (invoice: any) => {
    setSelectedInvoice(invoice);
    setShowPrintModal(true);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
      <div className="space-y-3">

        {/* The patient's account in one card: what was billed, what is owed after the advance (the
            same figure as the "₹ due" chip in the header), and the advance held. Each balance keeps
            its action beside the number that motivates it. */}
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border rounded-lg border border-border overflow-hidden">
          <div className="bg-card px-4 py-3 min-w-0">
            <dt className="text-xs text-muted-foreground">Total billed</dt>
            <dd className="mt-0.5 font-mono text-xl font-semibold tabular-nums text-foreground">{formatCurrency(totalBilled)}</dd>
          </div>

          <div className="bg-card px-4 py-3 min-w-0 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <dt className="text-xs text-muted-foreground">Balance due</dt>
              <dd className="mt-0.5">
                <span className={cn("font-mono text-xl font-semibold tabular-nums", outstanding > 0 ? "rounded bg-highlight px-1.5 text-highlight-foreground" : "text-foreground")}>
                  {formatCurrency(outstanding)}
                </span>
                {/* Bills still open although nothing is owed: the advance covers them, it just isn't applied yet */}
                {pendingAmount > outstanding && availableAdvance > 0 && (
                  <span className="block truncate text-xs text-muted-foreground" title="Use advance on the bill to pay it from the advance balance">
                    {formatCurrency(pendingAmount)} unpaid on bills · {outstanding > 0 ? `${formatCurrency(availableAdvance)} covered by advance` : 'covered by advance'}
                  </span>
                )}
              </dd>
            </div>
            {outstanding > 0 && canWrite && (
                <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => setShowSettleModal(true)}>
                  Settle
                </Button>
            )}
          </div>

          {/* Two buttons don't fit beside the figure on a narrow card: they drop below it */}
          <div className="bg-card px-4 py-3 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="min-w-[8rem] flex-1">
              <dt className="text-xs text-muted-foreground whitespace-nowrap">Advance balance</dt>
              <dd className="mt-0.5 font-mono text-xl font-semibold tabular-nums text-foreground">{formatCurrency(availableAdvance)}</dd>
            </div>
            <div className="flex items-center gap-1">
              {can('billing:refund') && availableAdvance > 0 && (
                  <Button size="sm" variant="ghost" className="flex-shrink-0" onClick={() => setShowRefundAdvance(true)}>
                    Refund
                  </Button>
              )}
              {canWrite && (
                  <Button size="sm" variant="outline" className="flex-shrink-0 gap-1" onClick={() => setShowDepositModal(true)}>
                    <Plus className="w-3.5 h-3.5" /> Add advance
                  </Button>
              )}
            </div>
          </div>
        </dl>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <h2 className="text-sm font-semibold text-foreground">
            Bills <span className="ml-1 font-normal text-muted-foreground tabular-nums">{invoices.length}</span>
          </h2>
          {canWrite && (
              <Button onClick={() => setShowCreateModal(true)} size="sm" className="gap-1.5">
                <Plus className="w-4 h-4" /> New bill
              </Button>
          )}
        </div>

        {/* Bills list — the same table as the Billing page, without the Patient column */}
        <BillingListContainer
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            dateFilter="all"
            onDateChange={() => {}}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            amountRange={amountRange}
            onAmountRangeChange={setAmountRange}
            variant="patient"
            invoices={invoices}
            filteredInvoices={filteredInvoices}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onCreateInvoice={canWrite ? () => setShowCreateModal(true) : undefined}
        >
          <InvoiceTable
              hidePatient
              invoices={filteredInvoices.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
              selectedId={showViewModal ? selectedInvoice?.id : null}
              onView={handleViewInvoice}
              onEdit={canWrite ? handleEditInvoice : undefined}
              onPrint={handlePrintInvoice}
              onPayment={canWrite ? handlePaymentAction : undefined}
              onApplyAdvance={canWrite && availableAdvance > 0 ? handleApplyAdvance : undefined}
          />
        </BillingListContainer>

        {/* Advances — only once the patient has had one */}
        {advanceList.length > 0 && (
          <section aria-label="Advances" className="space-y-2">
            <h2 className="text-sm font-semibold text-foreground">
              Advances <span className="ml-1 font-normal text-muted-foreground tabular-nums">{advanceList.length}</span>
            </h2>
            <div className="rounded-lg border border-border bg-card divide-y divide-divider">
              {advanceList.map(r => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-3 min-h-10 py-1.5 text-sm">
                  <div className="min-w-0">
                    <span className="font-medium">{r.refund ? 'Refund' : 'Deposit'}</span>
                    <span className="text-muted-foreground"> · {ADVANCE_MODE_LABEL[r.payment_mode ?? ''] ?? r.payment_mode}{r.admission_number ? ` · ${r.admission_number}` : ''}</span>
                    <span className="block text-xs text-muted-foreground">{formatDateTime(r.date!)}{r.reference_number ? ` · Ref ${r.reference_number}` : ''}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={cn('font-mono tabular-nums whitespace-nowrap', r.refund && 'text-destructive')}>
                      {r.refund ? `− ${formatCurrency(r.amount)}` : formatCurrency(r.amount)}
                    </span>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" title="Print receipt"
                      aria-label={r.refund ? 'Print refund receipt' : 'Print advance receipt'} onClick={() => setDepositReceipt(r)}>
                      <Printer className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Modals */}
        {showCreateModal && (
            <MedicalBillingModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                patient={patient}
                services={services}
                onCreateInvoice={handleCreateInvoice}
                loading={loading}
                clinicId={clinicId}
                doctorId={doctorId}
            />
        )}

        <InvoiceViewModal
            isOpen={showViewModal}
            onClose={() => { setShowViewModal(false); setSelectedInvoice(null); }}
            invoice={selectedInvoice}
            onPrint={handlePrintInvoice}
            onPayment={canWrite ? handlePaymentAction : undefined}
            onRefunded={reloadAccount}
            variant="patient"
        />

        <PaymentRecordModal
            isOpen={showPaymentModal}
            onClose={() => { setShowPaymentModal(false); setSelectedInvoice(null); }}
            invoice={selectedInvoice}
            onRecordPayment={handleRecordPayment}
            loading={loading}
        />

        <PrintBillPreviewModal
            open={showPrintModal}
            invoice={selectedInvoice}
            onClose={() => { setShowPrintModal(false); setSelectedInvoice(null); }}
        />

        <InvoiceEditModal
            invoice={selectedInvoice ? {
              ...selectedInvoice,
              patient_id:      selectedInvoice.patient_id      || patientId,
              patient_address: selectedInvoice.patient_address || patient.address,
              doctor_id:       selectedInvoice.doctor_id       || doctorId,
              clinic_id:       selectedInvoice.clinic_id       || clinicId,
              subtotal:        selectedInvoice.subtotal        || selectedInvoice.total_amount,
              discount_amount: selectedInvoice.discount_amount || 0,
              tax_amount:      selectedInvoice.tax_amount      || 0,
              due_date:        selectedInvoice.due_date        || undefined,
              notes:           selectedInvoice.notes           || undefined,
              items:           selectedInvoice.items           || []
            } : null}
            isOpen={showEditModal}
            onClose={() => { setShowEditModal(false); setSelectedInvoice(null); }}
            onSave={handleUpdateInvoice}
            services={services}
            loading={loading}
        />

        <SettleBalanceModal
            isOpen={showSettleModal}
            onClose={() => setShowSettleModal(false)}
            patientId={patientId}
            clinicId={clinicId}
            outstandingAmount={outstanding}
            onSettled={reloadAccount}
        />

        <RecordDepositModal
            isOpen={showDepositModal}
            onClose={() => setShowDepositModal(false)}
            patientId={patientId}
            clinicId={clinicId}
            onRecorded={(d, print) => {
              loadDeposits();
              if (print) setDepositReceipt({ patient_id: patientId, clinic_id: clinicId, doctor_id: doctorId, amount: d.amount,
                                  payment_mode: d.payment_mode, reference_number: d.reference_number, receipt_number: d.receipt_number });
            }}
        />

        <PrintDepositReceiptModal open={!!depositReceipt} deposit={depositReceipt} onClose={() => setDepositReceipt(null)} />

        <RefundDialog
            patientId={patientId}
            clinicId={clinicId}
            doctorId={doctorId}
            spare={availableAdvance}
            open={showRefundAdvance}
            onClose={() => setShowRefundAdvance(false)}
            onDone={reloadAccount}
        />

        <AddAppointmentModal
            isOpen={showFollowUpModal}
            onClose={() => setShowFollowUpModal(false)}
            patientId={patientId}
            showPatientSelection={false}
            title="Book follow-up"
            onAdded={() => setShowFollowUpModal(false)}
        />
      </div>
  );
}